// ========================
// TYPES AND CONSTANTS
// ========================

// Define valid currency types with runtime validation
const VALID_CURRENCIES = ['EUR', 'XAF', 'XOF'] as const;
type CurrencyCode = typeof VALID_CURRENCIES[number];

// Strongly typed conversion rates
type ConversionRates = Record<CurrencyCode, Partial<Record<CurrencyCode, number>>>;

// Fixed conversion rates
const FIXED_RATES: ConversionRates = {
  EUR: { XAF: 655.96, XOF: 655.96 },
  XAF: { EUR: 1/655.96, XOF: 1 },
  XOF: { EUR: 1/655.96, XAF: 1 }
};

// ========================
// VALIDATION UTILITIES
// ========================

// Type guard for currency codes
function isCurrencyCode(currency: string): currency is CurrencyCode {
  return VALID_CURRENCIES.includes(currency.toUpperCase() as CurrencyCode);
}

// Validate and normalize currency code
function validateCurrency(currency: string): CurrencyCode {
  const normalized = currency.toUpperCase();
  if (!isCurrencyCode(normalized)) {
    throw new Error(`Invalid currency code: ${currency}`);
  }
  return normalized;
}

// ========================
// CORE CONVERSION LOGIC
// ========================

// Helper to safely get rates with proper typing
function getConversionRate(base: CurrencyCode, to: CurrencyCode): number | null {
  return FIXED_RATES[base]?.[to] ?? null;
}

// Main conversion function
export function ConvertPrice(base: string, to: string, amount: number): Promise<number> {
  try {
    // Enhanced validation
    const validatedAmount = Number(amount);
    if (isNaN(validatedAmount)) {
      return Promise.resolve(0); // Return 0 instead of throwing error
    }

    // Normalize and validate currencies
    const normalizedBase = validateCurrency(base);
    const normalizedTo = validateCurrency(to);

    // Same currency shortcut
    if (normalizedBase === normalizedTo) {
      return Promise.resolve(amount);
    }

    // Get and validate rate
    const rate = getConversionRate(normalizedBase, normalizedTo);
    if (rate === null) {
      throw new Error(`No conversion rate for ${base}→${to}`);
    }

    return Promise.resolve(amount * rate);
  } catch (error) {
    console.error("Conversion error:", error);
    return Promise.resolve(0); // Fallback to 0
  }
}

// ========================
// FORMATTING UTILITIES
// ========================

// Format currency with symbol and proper decimals
function formatCurrency(amount: number, currency: CurrencyCode): string {
  const formatter = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    minimumFractionDigits: currency === 'EUR' ? 2 : 0,
    maximumFractionDigits: currency === 'EUR' ? 2 : 0
  });
  return formatter.format(amount);
}

// Adjust precision without currency symbol
function adjustPrecision(value: number, currency: CurrencyCode): string {
  return currency === 'EUR' ? value.toFixed(2) : Math.round(value).toString();
}

// ========================
// PUBLIC API FUNCTIONS
// ========================

// Conversion with currency symbol formatting
export async function ConvertedDeviseWithMoneySign(
  from: string = "XAF",
  to: string,
  amount: number
): Promise<string> {
  if (from === to) {
    return formatCurrency(amount, validateCurrency(to));
  }
  
  try {
    const converted = await ConvertPrice(from, to, amount);
    return formatCurrency(converted, validateCurrency(to));
  } catch (error) {
    console.error('Conversion failed, using fallback:', error);
    return formatCurrency(amount, validateCurrency(from));
  }
}

// Conversion without currency symbol
export async function ConvertedDeviseWithoutMoneySign(
  from: string = "XAF",
  to: string,
  amount: number
): Promise<string> {
  try {
    const converted = await ConvertPrice(from, to, amount);
    return adjustPrecision(converted, validateCurrency(to));
  } catch (error) {
    console.error('Conversion failed, using fallback:', error);
    return adjustPrecision(amount, validateCurrency(from));
  }
}

// Alias for promise-based version
export const ConvertedDeviseWithoutMoneySignPromise = ConvertedDeviseWithoutMoneySign;

// Formatted conversion with default EUR base
export function ConvertPriceWithFormat(base: string = "EUR", to: string, amount: number): Promise<string> {
  return ConvertedDeviseWithMoneySign(base, to, amount);
}

// ========================
// CURRENCY CONVERT CLASS
// ========================

export class CurrencyConvert {
  async convert(from: string, to: string, amount: number): Promise<number> {
    return ConvertPrice(from, to, amount);
  }

  getConvertedValue(from: string, to: string, amount: number): Promise<number> {
    return this.convert(from, to, amount);
  }

  async convertDevise(from: string, to: string): Promise<{[key: string]: number}> {
    const base = validateCurrency(from);
    const target = validateCurrency(to);
    
    if (base === target) {
      return { [target]: 1 };
    }
    
    const rate = getConversionRate(base, target);
    if (rate === null) {
      throw new Error(`No conversion rate for ${from}→${to}`);
    }
    
    return { [target]: rate };
  }
}

// ========================
// HELPER FUNCTIONS
// ========================

// Helper for backward compatibility
export async function handleConversionWithoutSymbol(from: string, to: string, amount: number): Promise<string> {
  return ConvertedDeviseWithoutMoneySign(from, to, amount);
}